import { type RequestFailure, type ApiResponse } from '@owl-nest/api-client'
import { type CreateAddress, type Address, type UpdateAddress, api } from '@owl-nest/api-client/latest'
import * as addressUtils from '@owl-nest/address'
import * as Enum from '@owl-nest/legacy-enum'
import { type State } from '../../types'
import { type PromiseAction } from '../../requestMiddleware'
import { Either } from '@owl-nest/monad'

export const TYPE = Enum.create({
  CREATE_REQUEST: 'CREATE_ADDRESS_REQUEST',
  CREATE_FAILURE: 'CREATE_ADDRESS_FAILURE',
  CREATE_SUCCESS: 'CREATE_ADDRESS_SUCCESS',

  REMOVE_REQUEST: 'REMOVE_ADDRESS_REQUEST',
  REMOVE_FAILURE: 'REMOVE_ADDRESS_FAILURE',
  REMOVE_SUCCESS: 'REMOVE_ADDRESS_SUCCESS',

  UPDATE_REQUEST: 'UPDATE_ADDRESS_REQUEST',
  UPDATE_FAILURE: 'UPDATE_ADDRESS_FAILURE',
  UPDATE_SUCCESS: 'UPDATE_ADDRESS_SUCCESS',

  LOAD_REQUEST: 'LOAD_ADDRESSES_REQUEST',
  LOAD_FAILURE: 'LOAD_ADDRESSES_FAILURE',
  LOAD_SUCCESS: 'LOAD_ADDRESSES_SUCCESS',
})
export type TYPE = Enum.ValueOf<typeof TYPE>

type CreateAddressRequest = { type: typeof TYPE.CREATE_REQUEST; address: CreateAddress }
type CreateAddressFailure = { type: typeof TYPE.CREATE_FAILURE; error: RequestFailure }
type CreateAddressSuccess = { type: typeof TYPE.CREATE_SUCCESS; success: Address<string> }

type RemoveAddressRequest = { type: typeof TYPE.REMOVE_REQUEST; addressId: number }
type RemoveAddressFailure = { type: typeof TYPE.REMOVE_FAILURE; error: RequestFailure; addressId: number }
type RemoveAddressSuccess = { type: typeof TYPE.REMOVE_SUCCESS; addressId: number }

type UpdateAddressRequest = { type: typeof TYPE.UPDATE_REQUEST; address: UpdateAddress }
type UpdateAddressFailure = { type: typeof TYPE.UPDATE_FAILURE; error: RequestFailure }
type UpdateAddressSuccess = { type: typeof TYPE.UPDATE_SUCCESS; success: Address<string> }

type LoadAddressesRequest = { type: typeof TYPE.LOAD_REQUEST; userId: number }
type LoadAddressesFailure = { type: typeof TYPE.LOAD_FAILURE; error: RequestFailure }
type LoadAddressesSuccess = { type: typeof TYPE.LOAD_SUCCESS; success: Address<string>[] }

export type AddressAction =
  | CreateAddressRequest
  | CreateAddressFailure
  | CreateAddressSuccess
  | RemoveAddressRequest
  | RemoveAddressFailure
  | RemoveAddressSuccess
  | UpdateAddressRequest
  | UpdateAddressFailure
  | UpdateAddressSuccess
  | LoadAddressesRequest
  | LoadAddressesFailure
  | LoadAddressesSuccess

export function create<EXTRA_FIELDS extends string = ''>(
  address: CreateAddress,
  extraFields: EXTRA_FIELDS[] = [],
): PromiseAction<State<unknown>, ApiResponse<Address<EXTRA_FIELDS>>> {
  return {
    types: [TYPE.CREATE_REQUEST, TYPE.CREATE_SUCCESS, TYPE.CREATE_FAILURE],
    promise: async (_, state) => {
      const identity = state.user.identity
      if (identity.type !== 'loggedin') {
        return Either.left<RequestFailure, Address<EXTRA_FIELDS>>({
          status: 401,
          url: '/me',
          body: {},
          doNotRedirectToSignIn: true,
        })
      }
      return addressUtils.add(identity.authenticatedUser.id, address, extraFields)
    },
  }
}

export function remove(addressId: number): PromiseAction<State<unknown>, ApiResponse<void>, { addressId: number }> {
  return {
    types: [TYPE.REMOVE_REQUEST, TYPE.REMOVE_SUCCESS, TYPE.REMOVE_FAILURE],
    promise: async () => {
      return addressUtils.remove(addressId)
    },
    addressId,
  }
}

export function update<EXTRA_FIELDS extends string = ''>(
  addressId: number,
  address: UpdateAddress,
  extraFields: EXTRA_FIELDS[] = [],
): PromiseAction<State<unknown>, ApiResponse<Address<EXTRA_FIELDS>>> {
  return {
    types: [TYPE.UPDATE_REQUEST, TYPE.UPDATE_SUCCESS, TYPE.UPDATE_FAILURE],
    promise: async () => {
      return addressUtils.update<EXTRA_FIELDS>(addressId, address, extraFields)
    },
  }
}

export function load<EXTRA_FIELDS extends string = ''>(
  extraFields: EXTRA_FIELDS[] = [],
): PromiseAction<State<unknown>, ApiResponse<Address<EXTRA_FIELDS>[]> | undefined> {
  return {
    types: [TYPE.LOAD_REQUEST, TYPE.LOAD_SUCCESS, TYPE.LOAD_FAILURE],
    promise: async (_, state) => {
      const identity = state.user.identity
      if (identity.type !== 'loggedin') {
        console.error("can't load addresses if user is not loggedin")
        return
      }
      return addressUtils.get(identity.authenticatedUser.id, extraFields)
    },
  }
}
